import { SlotCanvasLayout } from "@/components/ai-elements/slot-canvas";
import { ArtifactShareAction } from "./ArtifactShareAction";
import { ArtifactPageContent, artifactSubtitle } from "./ArtifactPageContent";

export interface ArtifactCanvasBodyProps {
  /** The artifact on screen, for the Share control. */
  artifactId: string;
  title: string;
  /** URL the frame loads the version on screen from; see `artifactFrameSrc`. */
  contentSrc?: string;
  /** The same bytes, presigned to arrive as a named file. */
  downloadUrl?: string;
  /** Version being shown, which keys the iframe so a reload actually happens. */
  version?: number;
  /** The artifact's current version, to say when an older one is on screen. */
  latestVersion?: number;
  /** True until the artifact's details have ever loaded. */
  isPreparing: boolean;
  /** True when the artifact is gone (deleted, or never belonged to us). */
  isMissing: boolean;
}

/**
 * Body of the artifact canvas: the page itself, and the one Share control
 * that shares it or saves it as a file.
 *
 * The page is fetched from an origin that holds none of this app's cookies, so
 * its scripts have nothing to reach: straight from object storage, or, behind
 * the render flag, from the artifact domain the render route redirects to.
 * The download always points straight at object storage.
 */
export function ArtifactCanvasBody({
  artifactId,
  title,
  contentSrc,
  downloadUrl,
  version,
  latestVersion,
  isPreparing,
  isMissing,
}: ArtifactCanvasBodyProps) {
  return (
    <SlotCanvasLayout
      title={title}
      subtitle={artifactSubtitle(version, latestVersion)}
      // Gated on there being a page to share or keep, not merely a URL: a host
      // that had one before it could render would otherwise offer a download
      // beside the "opening your artifact" splash.
      headerActions={isPreparing || isMissing
        ? undefined
        : (
          <ArtifactShareAction
            artifactId={artifactId}
            title={title}
            surface="canvas"
            version={version}
            downloadUrl={downloadUrl}
          />
        )}
      bodyClassName="overflow-hidden"
    >
      <ArtifactPageContent
        title={title}
        contentSrc={contentSrc}
        version={version}
        isPreparing={isPreparing}
        isMissing={isMissing}
      />
    </SlotCanvasLayout>
  );
}
